import { useEffect, useMemo, useRef, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import {
  BookOpen,
  Bot,
  Copy,
  Download,
  FileText,
  Layers,
  LockKeyhole,
  Network,
  X,
} from "lucide-react";
import { useApp } from "@/context/AppContext";
import SourceContent, { sourceExplanation } from "./SourceContent";
import { relatedSources } from "./sourceRelations";

export const sourceDate = (value) =>
  value && Number.isFinite(Date.parse(value))
    ? new Date(value).toLocaleString()
    : "Not reported";
export function SourcePreview({ entry, compact = false }) {
  const Icon =
    entry.kind === "skill"
      ? Layers
      : entry.kind === "memory" || entry.kind === "preference"
        ? BookOpen
        : FileText;
  return (
    <div
      className={`solar-document ${compact ? "solar-document-compact" : ""}`}
      aria-hidden="true"
    >
      <span>
        <Icon size={14} />
        {entry.kind === "learning"
          ? "Saved learning"
          : entry.kind === "skill"
            ? "Agent skill"
            : entry.kind === "file"
              ? "Source file"
              : entry.kind === "knowledge"
                ? "Knowledge"
                : entry.kind}
      </span>
      <strong>{entry.title}</strong>
      <p>{entry.summary}</p>
      <div className="solar-document-lines">
        <i />
        <i />
        <i />
      </div>
      <small>{entry.sourceLabel}</small>
    </div>
  );
}

export default function BrainSourceDialog({
  entry,
  collection,
  clientSlug,
  agentName,
  workspaceName,
  entries = [],
  observedAt,
  onClose,
  onLocate,
}) {
  const { api } = useApp();
  const [tab, setTab] = useState("content");
  const [result, setResult] = useState({
    status: "loading",
    content: null,
    url: null,
  });
  const [copied, setCopied] = useState("");
  const [refresh, setRefresh] = useState(0);
  const related = useMemo(
    () =>
      relatedSources(
        entry,
        entries,
        `${workspaceName || ""} ${agentName || ""}`,
      ),
    [entry, entries, workspaceName, agentName],
  );
  const explanation = sourceExplanation(entry, result.content);
  const locate = (id) => {
    onClose();
    onLocate(id);
  };
  useEffect(() => {
    const refreshSource = () => setRefresh((n) => n + 1);
    window.addEventListener("focus", refreshSource);
    return () => window.removeEventListener("focus", refreshSource);
  }, []);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const returnFocus = useRef(
    typeof document !== "undefined" ? document.activeElement : null,
  );
  useEffect(() => {
    let active = true,
      url = null;
    setResult({ status: "loading", content: null, url: null });
    (async () => {
      try {
        let content = entry.content ?? null;
        if (entry.origin === "publication") {
          const data = await api.getBrainEntry(entry.view, entry.recordId);
          if (
            data.clientSlug !== clientSlug ||
            data.view !== entry.view ||
            data.entry?.id !== entry.recordId
          )
            throw new Error("scope");
          // Shared source rights remain citation-only, even if a malformed
          // response contains text. Never turn a citation into a raw export.
          content =
            entry.view === "brand" && data.entry.contentAvailable
              ? data.entry.content
              : null;
        } else if (entry.origin === "file") {
          const data = await api.getAgentFile(entry.recordId),
            file = data.file;
          if (
            data.clientSlug !== clientSlug ||
            file?.id !== entry.recordId ||
            ![
              "text/plain",
              "text/markdown",
              "text/csv",
              "application/json",
              "application/pdf",
              "image/png",
              "image/jpeg",
              "image/webp",
            ].includes(file.mimeType)
          )
            throw new Error("scope");
          const bytes = Uint8Array.from(atob(file.contentBase64), (c) =>
            c.charCodeAt(0),
          );
          if (bytes.length !== file.sizeBytes || bytes.length > 2097152)
            throw new Error("size");
          if (!active) return;
          url = URL.createObjectURL(new Blob([bytes], { type: file.mimeType }));
          content =
            file.mimeType.startsWith("text/") ||
            file.mimeType === "application/json"
              ? new TextDecoder().decode(bytes)
              : null;
        }
        if (active) setResult({ status: "ready", content, url });
      } catch {
        if (active) setResult({ status: "error", content: null, url: null });
      }
    })();
    return () => {
      active = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [api, clientSlug, entry, refresh]);
  const copy = async () => {
    setCopied("");
    try {
      await navigator.clipboard.writeText(
        `Use your ${collection.title} record “${entry.title}” (${entry.citation || entry.sourcePath || entry.sourceLabel}). Explain how it applies to our brand, and cite the source. Only use material you can access.`,
      );
      setCopied("Prompt copied");
    } catch {
      setCopied("Could not copy. Please try again.");
    }
  };
  const downloadUpload = async () => {
    try {
      const data = await api.downloadKnowledgeUpload(entry.upload.id);
      if (!mounted.current) return;
      const url = URL.createObjectURL(data.blob ?? data);
      const a = document.createElement("a");
      a.href = url;
      a.download = entry.upload.name || entry.title;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      setCopied("Could not download. Access may have changed.");
    }
  };
  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="solar-dialog-overlay" />
        <Dialog.Content
          className="solar-source-dialog"
          style={{ "--solar-tone": entry.color }}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            returnFocus.current?.focus?.();
          }}
        >
          <aside className="solar-popup-rail">
            <div className="solar-popup-identity">
              <span className="solar-gloss solar-monogram">
                {entry.title
                  .split(/\s+/)
                  .slice(0, 2)
                  .map((s) => s[0])
                  .join("")}
              </span>
              <div>
                <strong>{entry.title}</strong>
                <small>
                  {entry.kind === "learning"
                    ? "Saved learning"
                    : entry.kind === "knowledge"
                      ? "Knowledge source"
                      : entry.kind}
                </small>
              </div>
            </div>
            <span className="solar-scope-chip">
              {entry.view === "shared" ? (
                <BookOpen size={12} />
              ) : (
                <LockKeyhole size={12} />
              )}
              {entry.scope}
            </span>
            <nav aria-label="Source card sections">
              {[
                ["content", "Saved content", FileText],
                ["connections", "Source & history", Network],
              ].map(([id, label, Icon]) => (
                <button
                  key={id}
                  type="button"
                  aria-pressed={tab === id}
                  onClick={() => setTab(id)}
                >
                  <span>
                    <Icon size={15} />
                  </span>
                  {label}
                </button>
              ))}
            </nav>
            <div className="solar-popup-agent">
              <Bot size={22} />
              <span>
                {agentName}
                <small>Your Hermes agent</small>
              </span>
            </div>
          </aside>
          <div className="solar-popup-main">
            <header>
              <span>
                {collection.title} / {entry.sourceLabel}
              </span>
              <Dialog.Title>{entry.title}</Dialog.Title>
              <Dialog.Description className="sr-only">
                Saved source details and its connection to your agent.
              </Dialog.Description>
            </header>
            {result.status === "error" ? (
              <div className="solar-message" role="alert">
                <p>
                  This source could not be retrieved. Its access may have
                  changed.
                </p>
                <button type="button" onClick={() => setRefresh((n) => n + 1)}>
                  Try again
                </button>
              </div>
            ) : (
              <>
                {tab === "content" ? (
                  <div className="solar-saved-content">
                    <div className="solar-content-context">
                      <span className="solar-content-kind">
                        <BookOpen size={14} />
                        {explanation.label}
                      </span>
                      <p>{explanation.text}</p>
                    </div>
                    {result.status === "loading" ? (
                      <p role="status">Loading source…</p>
                    ) : result.content ? (
                      <>
                        {entry.origin === "file" &&
                        !["text/markdown", "text/plain"].includes(
                          entry.file?.mimeType,
                        ) ? (
                          <pre data-testid="brain-content">
                            {result.content}
                          </pre>
                        ) : (
                          <SourceContent
                            content={result.content}
                            kind={entry.kind}
                          />
                        )}
                        {entry.kind !== "skill" ? (
                          <details className="solar-original-text">
                            <summary>View original saved text</summary>
                            <pre>{result.content}</pre>
                          </details>
                        ) : null}
                      </>
                    ) : (
                      <div data-testid="brain-content-unavailable">
                        {entry.origin === "file" ? (
                          result.url &&
                          entry.file?.mimeType?.startsWith("image/") ? (
                            <img
                              className="solar-source-image"
                              src={result.url}
                              alt={entry.title}
                            />
                          ) : (
                            <p>Download the original to view this file.</p>
                          )
                        ) : (
                          <p className="solar-published-summary">
                            {entry.summary}
                          </p>
                        )}
                      </div>
                    )}
                    {entry.citation ? (
                      <p
                        className="solar-citation"
                        data-testid="brain-citation"
                      >
                        {entry.citation}
                      </p>
                    ) : null}
                    {result.url ? (
                      <a
                        href={result.url}
                        download={entry.file.name}
                        className="solar-primary"
                      >
                        <Download size={15} />
                        Download original
                      </a>
                    ) : null}
                    {entry.upload ? (
                      <button
                        type="button"
                        className="solar-primary"
                        onClick={downloadUpload}
                      >
                        <Download size={15} />
                        Download original
                      </button>
                    ) : null}
                    <section
                      className="solar-provenance"
                      aria-label="Where this comes from"
                    >
                      <h3>Where this comes from</h3>
                      <dl>
                        <div>
                          <dt>Source</dt>
                          <dd>{entry.sourceLabel}</dd>
                        </div>
                        {entry.sourcePath ? (
                          <div className="solar-provenance-path">
                            <dt>File in your agent’s workspace</dt>
                            <dd>
                              <code>{entry.sourcePath}</code>
                            </dd>
                          </div>
                        ) : null}
                        <div>
                          <dt>
                            {entry.origin === "runtime"
                              ? "Source file modified"
                              : "Published"}
                          </dt>
                          <dd>{sourceDate(entry.updatedAt)}</dd>
                        </div>
                        {entry.origin === "runtime" ? (
                          <div>
                            <dt>Read from your agent</dt>
                            <dd>{sourceDate(observedAt)}</dd>
                          </div>
                        ) : null}
                      </dl>
                    </section>
                    {related.length ? (
                      <section
                        className="solar-related"
                        aria-label="Related material"
                      >
                        <h3>Explore related material</h3>
                        <p>
                          Topic matches help you explore. They do not establish
                          where this note was learned.
                        </p>
                        {related.map(({ entry: row, linked, terms }) => (
                          <button
                            type="button"
                            key={row.id}
                            onClick={() => locate(row.id)}
                            aria-label={`Locate related source ${row.title}`}
                          >
                            <span
                              className="solar-related-icon"
                              style={{ color: row.color }}
                            >
                              <FileText size={18} />
                            </span>
                            <span>
                              <small>
                                {linked
                                  ? "Saved skill connection"
                                  : `Topic match · ${terms.join(", ")}`}
                              </small>
                              <strong>{row.title}</strong>
                              <span>{row.summary}</span>
                            </span>
                            <span aria-hidden="true">↗</span>
                          </button>
                        ))}
                      </section>
                    ) : null}
                  </div>
                ) : null}
                {tab === "connections" ? (
                  <div className="solar-source-connections">
                    <span className="solar-eyebrow">
                      Its place in your system
                    </span>
                    <ol>
                      <li>
                        <Bot />
                        <span>
                          <small>Your agent</small>
                          <strong>{agentName}</strong>
                        </span>
                      </li>
                      <li>
                        <Network />
                        <span>
                          <small>{collection.scope}</small>
                          <strong>{collection.title}</strong>
                        </span>
                      </li>
                      <li>
                        <FileText />
                        <span>
                          <small>
                            {entry.kind === "learning"
                              ? "Learned reference"
                              : "Saved source"}
                          </small>
                          <strong>{entry.title}</strong>
                        </span>
                      </li>
                    </ol>
                    {entry.parentSkill ? (
                      <p>
                        Saved with the{" "}
                        <strong>{entry.parentSkill.replace(/-/g, " ")}</strong>{" "}
                        skill.
                      </p>
                    ) : null}
                    <dl>
                      <div>
                        <dt>
                          {entry.origin === "runtime"
                            ? "Source file modified"
                            : "Published"}
                        </dt>
                        <dd>{sourceDate(entry.updatedAt)}</dd>
                      </div>
                      {entry.sourcePath ? (
                        <div>
                          <dt>Source location</dt>
                          <dd>{entry.sourcePath}</dd>
                        </div>
                      ) : null}
                    </dl>
                    {entry.origin === "runtime" ? (
                      <div className="solar-source-history-note">
                        <h3>About this history</h3>
                        <p>
                          The timestamp records a change to the source file, not
                          when this individual note was learned. This snapshot
                          does not include its originating conversation or a
                          revision history.
                        </p>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </>
            )}
            <footer>
              <button type="button" onClick={onClose}>
                <Network size={14} />
                Back to constellation
              </button>
              {result.status === "ready" ? (
                <button type="button" onClick={copy}>
                  <Copy size={13} />
                  Copy a prompt
                </button>
              ) : null}
              <span role="status">{copied}</span>
            </footer>
          </div>
          <Dialog.Close
            className="solar-popup-close"
            aria-label="Close source card"
          >
            <X size={18} />
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
